import { defineAppPermissionCatalog } from "./types";

export const usersPermissionCatalog = defineAppPermissionCatalog({
  appId: "users",
  appName: "Users",
  appDescription: "Controls user account, security group, and permission administration.",
  appAccessPermissionIds: ["USERS_APP_VIEW"],
  adminPermissionIds: ["COMMON_ADMIN"],
  permissions: [
    {
      permissionId: "USERS_APP_VIEW",
      title: "Access users app",
      description: "Allows users to open the Users application.",
      category: "Application access",
      impliedBy: ["COMMON_ADMIN"],
      usedIn: [{ file: ".env.example", context: "Users app access permission" }],
    },
    {
      permissionId: "USERS_LIST_VIEW",
      title: "View users",
      description: "Allows users to open the Users list and view other application users.",
      category: "User management",
      impliedBy: ["COMMON_ADMIN"],
      usedIn: [
        { file: "src/router/index.ts", context: "User details route access", routePath: "/user-details/:partyId" },
        { file: "src/components/Tabs.vue", context: "Users tab visibility" },
      ],
    },
    {
      permissionId: "APP_USER_CREATE",
      title: "Create users",
      description: "Allows users to create a new application user and complete user setup.",
      category: "User management",
      impliedBy: ["COMMON_ADMIN"],
      usedIn: [
        { file: "src/router/index.ts", context: "Create user and confirmation routes", routePath: "/create-user" },
        { file: "src/views/Users.vue", context: "Create user action" },
      ],
    },
    {
      permissionId: "APP_UPDT_BLOCK_LOGIN",
      title: "Enable or disable login",
      description: "Allows users to block or restore another user's login and force logout active sessions.",
      category: "Account access",
      impliedBy: ["COMMON_ADMIN"],
      usedIn: [{ file: "src/views/UserDetails.vue", context: "Login status and force logout controls" }],
    },
    {
      permissionId: "APP_UPDT_PASSWORD",
      title: "Reset passwords",
      description: "Allows users to reset another user's password.",
      category: "Account access",
      impliedBy: ["COMMON_ADMIN"],
      usedIn: [
        { file: "src/views/UserDetails.vue", context: "Reset password action" },
        { file: "src/components/ResetPasswordModal.vue", context: "Password reset save action" },
      ],
    },
    {
      permissionId: "APP_SECURITY_GROUP_CREATE",
      title: "Assign security groups",
      description: "Allows users to create security groups and assign or remove security groups from users.",
      category: "Security groups",
      impliedBy: ["COMMON_ADMIN"],
      usedIn: [
        { file: "src/views/AppPermissions.vue", context: "Create security group action" },
        { file: "src/views/UserDetails.vue", context: "Assign security groups to a user" },
      ],
    },
    {
      permissionId: "APP_PERMISSION_VIEW",
      title: "View permissions",
      description: "Allows users to open permission administration pages.",
      category: "Permission administration",
      impliedBy: ["COMMON_ADMIN"],
      usedIn: [{ file: "src/router/index.ts", context: "Permissions tab route", routePath: "/tabs/app-permissions" }],
    },
    {
      permissionId: "APP_PERMISSION_CREATE",
      title: "Grant permissions to groups",
      description: "Allows users to add permission assignments to security groups.",
      category: "Permission administration",
      impliedBy: ["COMMON_ADMIN"],
      usedIn: [{ file: "src/components/PermissionItems.vue", context: "Checked permission assignment" }],
    },
    {
      permissionId: "APP_PERMISSION_UPDATE",
      title: "Remove permissions from groups",
      description: "Allows users to expire permission assignments from security groups.",
      category: "Permission administration",
      impliedBy: ["COMMON_ADMIN"],
      usedIn: [{ file: "src/components/PermissionItems.vue", context: "Unchecked permission assignment" }],
    },
    {
      permissionId: "APP_UPDT_PRODUCT_STORE_CONFG",
      title: "Assign product stores",
      description: "Allows users to update which product stores another user can access.",
      category: "Store access",
      impliedBy: ["COMMON_ADMIN"],
      usedIn: [{ file: "src/views/UserDetails.vue", context: "Product store assignment controls" }],
    },
    {
      permissionId: "APP_UPDT_FULFILLMENT_FACILITY",
      title: "Assign fulfillment facilities",
      description: "Allows users to update which fulfillment facilities another user can access.",
      category: "Facility access",
      impliedBy: ["COMMON_ADMIN"],
      usedIn: [{ file: "src/views/UserDetails.vue", context: "Fulfillment facility assignment controls" }],
    },
    {
      permissionId: "APP_UPDT_PICKER_CONFG",
      title: "Update picker configuration",
      description: "Allows users to update picker configuration on another user profile.",
      category: "Fulfillment configuration",
      impliedBy: ["COMMON_ADMIN"],
      usedIn: [{ file: "src/views/UserDetails.vue", context: "Picker configuration controls" }],
    },
    {
      permissionId: "APP_SUPER_USER",
      title: "Manage super users",
      description: "Allows users to view and manage users associated with the SUPER security group.",
      category: "Sensitive access",
      impliedBy: ["COMMON_ADMIN"],
      usedIn: [{ file: "src/views/UserDetails.vue", context: "SUPER security group visibility and assignment" }],
    },
    {
      permissionId: "APP_PWA_STANDALONE_ACCESS",
      title: "Open standalone app controls",
      description: "Allows users to access standalone application actions from settings.",
      category: "Application access",
      impliedBy: ["COMMON_ADMIN"],
      usedIn: [{ file: "src/views/Settings.vue", context: "Standalone launchpad action" }],
    },
  ],
} as const);
